import React, { useCallback, useEffect, useState } from 'react';

// Internal Imports
import DataGrid from '../../common/components/DataGrid/DataGrid';
import MenuLayout from '../../common/components/MenuLayout/MenuLayout';
import ConfirmationModal from '../../common/components/Modal/CorfimationModal';
import Modal from '../../common/components/Modal/Modal';
import Paint from '../../common/components/Paint/Paint';
import { COLORS } from '../../common/constants/colors';
import { getError, openNotification, tryCatch } from '../../common/constants/Utilities';
import { Mode } from '../../common/types/AllTypes';
import ViewContainer from '../../common/components/ViewContainer/ViewContainer';
import { useAuth } from '../../contexts/auth';
import { useForm } from '../../contexts/form';
import IColumn from '../../interfaces/IColumn';
import { IMenu } from '../../interfaces/IMenu';
import { IUserInformation } from '../../interfaces/IUser';
import { DeactiveUserById, getUserByID, getUsers } from '../../services/user';
import AddUpdateUser from './AddUpdateUser';
import './Usuarios.css';
import { LoadingIcon } from '../../common/constants/cssElements';

// Interfaces
interface Props {
    menu: IMenu
}

const Usuarios: React.FC<Props> = ({ menu }) => {
    const { user } = useAuth();
    const { setUserInformationForm, userInformationForm } = useForm();
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingAction, setLoadingAction] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [addUpdateModal, setAddUpdateModal] = useState<boolean>(false);
    const [mode, setMode] = useState<Mode>('Add');
    const [searchV, setSearchV] = useState<string>('');
    const [_listUser, set_ListUser] = useState<IUserInformation[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
    const [idDetail, setIdDetail] = useState<number | null>(null);
    // Consts
    const columns: IColumn[] = [
        {
            title: 'Usuario',
            dataIndex: '',
            render: (data: IUserInformation) => <nav>{data.nombreUsuario}</nav>
        },
        {
            title: 'Nombre',
            dataIndex: 'primerNombre',
        },
        {
            title: 'Apellido',
            dataIndex: 'primerApellido',
        },
        {
            title: 'Correo',
            dataIndex: 'correo',
        },
        {
            title: 'Puesto',
            dataIndex: '',
            render: (data: IUserInformation) => <nav>{data.usuarioProyecto?.puestoNombre}</nav>
        },
        {
            title: 'Departamento',
            dataIndex: '',
            render: (data: IUserInformation) => <nav>{data.usuarioProyecto?.departamentoNombre}</nav>
        },
        {
            title: <i className="fa-solid fa-rotate button_refresh" onClick={() => { handleGetUsersByUserID(); setSearchV('') }}></i>,
            dataIndex: '',
            render: (data: IUserInformation) => loadingDetail && idDetail === data.usuarioID ? <LoadingIcon color={COLORS.blue_active} size={'20px'} /> : <></>
        }
    ];

    // handles
    const handleGetUsersByUserID = () => {
        tryCatch(() => {
            setLoading(true);
            getUsers(user!.usuarioID)
                .then((response => {
                    set_ListUser(response.data);
                }))
                .catch((response) => {
                    openNotification('error', 'Error', getError(response?.msg));
                })
                .finally(() => setLoading(false));
        })
    }


    const handleGetUserByID = (user: IUserInformation) => {
        setUserInformationForm(undefined);
        if (!!user) {
            setIdDetail(user.usuarioID);
            setLoadingDetail(true);
            tryCatch(() => {
                getUserByID(user.usuarioID!)
                    .then(({ data }) => {
                        setUserInformationForm(data);
                    })
                    .catch((response) => {
                        openNotification('error', 'Error', getError(response?.msg));
                    })
                    .finally(() => setLoadingDetail(false))
            })
        }
    }

    const handleDeactiveUser = () => {
        tryCatch(() => {
            if (!!userInformationForm) {
                setLoadingAction(true);
                DeactiveUserById(userInformationForm)
                    .then((response) => {
                        openNotification('success', `Usuario (${userInformationForm.primerNombre} ${userInformationForm.primerApellido}) eliminado`, response.msg!);
                        set_ListUser(_listUser.filter(user => user.usuarioID !== userInformationForm.usuarioID!));
                    })
                    .catch((response) => {
                        openNotification('error', 'Error', getError(response?.msg));
                    })
                    .finally(() => {
                        setLoadingAction(false);
                        setDeleteModal(false);
                        setUserInformationForm(undefined);
                    })
            }
        })
    }


    // Methods
    const getUsersFiltered = useCallback((): IUserInformation[] => {
        let usersFiltered: IUserInformation[] = [];
        const valueLowerCase = searchV.toLowerCase();

        if (!!valueLowerCase) {
            usersFiltered = _listUser.filter((user) => user.usuarioProyecto!.departamentoNombre?.toLowerCase().includes(valueLowerCase) || user.primerNombre?.toLowerCase().includes(valueLowerCase) || user.primerApellido?.toLowerCase().includes(valueLowerCase) || user.nombreUsuario?.toLowerCase().includes(valueLowerCase) || user.cedula?.toLowerCase().includes(valueLowerCase) || user.codigoEmpleado?.toLowerCase().includes(valueLowerCase) || user.nombreUsuario?.toLowerCase().includes(valueLowerCase));
        } else {
            usersFiltered = _listUser;
        }

        return usersFiltered;
    }, [searchV, _listUser]);

    const openModalUpdate = () => {
        setMode('Update');
        setAddUpdateModal(true);
    }

    const openModalAdd = () => {
        setMode('Add');
        setUserInformationForm(undefined);
        setAddUpdateModal(true);
    }

    //Effects
    useEffect(() => {
        handleGetUsersByUserID();
    }, []);

    useEffect(() => {
        if (!addUpdateModal) {
            setSelectedRowKeys([]);
            setUserInformationForm(undefined);
        }
    }, [addUpdateModal]);

    return (
        <MenuLayout currentPage={menu.nombre}>
            <ViewContainer>
                <DataGrid
                    searchV={searchV}
                    selectedRowKeys={selectedRowKeys}
                    setSelectedRowKeys={setSelectedRowKeys}
                    gridProps={{ rowKey: (record: any) => record.usuarioGUID }}
                    columns={columns}
                    searchMethod={setSearchV}
                    menu={menu}
                    loading={loading}
                    dataSource={getUsersFiltered()}
                    setItemSelected={handleGetUserByID}
                    addAction={{ active: true, method: openModalAdd }}
                    deleteAction={{ active: !!userInformationForm, method: () => setDeleteModal(true) }}
                    updateAction={{ active: !!userInformationForm, method: openModalUpdate }}
                />
            </ViewContainer>


            <ConfirmationModal
                title={menu.nombre}
                visible={deleteModal}
                setVisible={setDeleteModal}
                question={`¿Seguro que deseas eliminar este Usuario (${userInformationForm?.primerNombre} ${userInformationForm?.primerApellido})?`}
                method={handleDeactiveUser}
                loading={loadingAction}
            />

            <Modal props={{ style: { top: '-10px' } }} title={<nav className="modal_title"> {mode === 'Add' ? 'Crear' : 'Modificar'}  <Paint color={COLORS.blue_main(1)}>Usuario</Paint></nav>} visible={addUpdateModal} setVisible={setAddUpdateModal} askBeforeClose={true}>
                <AddUpdateUser dataList={_listUser} setDataList={set_ListUser} mode={mode} setMode={setMode} setAddUpdateModal={setAddUpdateModal} />
            </Modal>
        </MenuLayout>
    );
}

export default Usuarios;